import React, { useState } from 'react';
import { 
  Search, 
  Filter, 
  X, 
  ChevronDown, 
  ChevronUp,
  SlidersHorizontal,
  CheckCircle,
  AlertTriangle,
  Clock,
  FileText
} from 'lucide-react';

const MobileSearchFilter = ({
  searchTerm,
  onSearchChange,
  filters,
  onFiltersChange,
  filterOptions,
  documentInsights,
  onClearFilters
}) => {
  const [showFilters, setShowFilters] = useState(false);
  const [showQuickFilters, setShowQuickFilters] = useState(true);

  const hasActiveFilters = Object.values(filters).some(f => f !== 'all');

  const quickFilterOptions = [
    {
      id: 'all',
      label: 'All',
      count: documentInsights.total,
      icon: FileText,
      color: 'text-gray-600',
      bgColor: 'bg-gray-100',
      borderColor: 'border-gray-200'
    },
    {
      id: 'expired',
      label: 'Expired',
      count: documentInsights.expired,
      icon: AlertTriangle,
      color: 'text-red-600',
      bgColor: 'bg-red-100',
      borderColor: 'border-red-200'
    },
    {
      id: 'expiring_soon',
      label: 'Expiring',
      count: documentInsights.expiringSoon,
      icon: Clock,
      color: 'text-amber-600',
      bgColor: 'bg-amber-100',
      borderColor: 'border-amber-200'
    },
    {
      id: 'valid',
      label: 'Valid',
      count: documentInsights.total - documentInsights.expired - documentInsights.expiringSoon,
      icon: CheckCircle,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
      borderColor: 'border-green-200'
    }
  ];

  const handleQuickFilter = (filterId) => {
    if (filterId === 'all') {
      onFiltersChange({
        entityType: 'all',
        type: 'all',
        status: 'all',
        expiration: 'all',
        appliesTo: 'all'
      });
    } else {
      onFiltersChange({
        ...filters,
        status: filterId
      });
    }
  };

  const getActiveQuickFilter = () => {
    if (filters.status === 'expired') return 'expired';
    if (filters.status === 'expiring_soon') return 'expiring_soon';
    if (filters.status === 'valid') return 'valid';
    if (!hasActiveFilters) return 'all';
    return null;
  };

  return (
    <div className="bg-white border-b border-gray-200 sticky top-0 z-20">
      {/* Search Bar */}
      <div className="p-4">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search documents..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="block w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
          />
          {searchTerm && (
            <button
              onClick={() => onSearchChange('')}
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
            >
              <X className="h-5 w-5 text-gray-400" />
            </button>
          )}
        </div>
      </div>

      {/* Quick Filter Pills */}
      {showQuickFilters && (
        <div className="px-4 pb-3">
          <div className="flex space-x-2 overflow-x-auto scrollbar-hide">
            {quickFilterOptions.map((option) => {
              const isActive = getActiveQuickFilter() === option.id;
              const IconComponent = option.icon;
              
              return (
                <button
                  key={option.id}
                  onClick={() => handleQuickFilter(option.id)}
                  className={`flex-shrink-0 inline-flex items-center px-3 py-2 rounded-full text-sm font-medium border transition-colors ${
                    isActive
                      ? `${option.bgColor} ${option.color} ${option.borderColor} ring-2 ring-opacity-50`
                      : 'bg-gray-100 text-gray-600 border-gray-200 hover:bg-gray-200'
                  }`}
                >
                  <IconComponent className="h-4 w-4 mr-1.5" />
                  <span>{option.label}</span>
                  <span className={`ml-1.5 px-1.5 py-0.5 text-xs rounded-full ${
                    isActive ? 'bg-white bg-opacity-70' : 'bg-gray-200'
                  }`}>
                    {option.count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Filter Controls */}
      <div className="px-4 pb-3 flex items-center justify-between">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`inline-flex items-center px-3 py-2 border rounded-lg text-sm font-medium transition-colors ${
            showFilters || hasActiveFilters
              ? 'border-blue-300 text-blue-700 bg-blue-50'
              : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
          }`}
        >
          <SlidersHorizontal className="h-4 w-4 mr-2" />
          Advanced
          {hasActiveFilters && (
            <span className="ml-2 inline-flex items-center justify-center w-2 h-2 bg-blue-600 rounded-full"></span>
          )}
          {showFilters ? <ChevronUp className="h-4 w-4 ml-1" /> : <ChevronDown className="h-4 w-4 ml-1" />}
        </button>

        {hasActiveFilters && (
          <button
            onClick={onClearFilters}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Advanced Filters */}
      {showFilters && (
        <div className="border-t border-gray-200 bg-gray-50">
          <div className="p-4 space-y-4">
            {/* Entity Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Entity Type
              </label>
              <select
                value={filters.entityType}
                onChange={(e) => onFiltersChange({ ...filters, entityType: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Types</option>
                {filterOptions.entityTypes.map(entityType => (
                  <option key={entityType} value={entityType}>
                    {entityType.charAt(0).toUpperCase() + entityType.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            {/* Document Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Document Type
              </label>
              <select
                value={filters.type}
                onChange={(e) => onFiltersChange({ ...filters, type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Types</option>
                {filterOptions.types.map(type => (
                  <option key={type} value={type}>
                    {type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </option>
                ))}
              </select>
            </div>

            {/* Entity Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Applies To
              </label>
              <select
                value={filters.appliesTo}
                onChange={(e) => onFiltersChange({ ...filters, appliesTo: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Entities</option>
                {filterOptions.appliesTo.map(name => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            </div>

            {/* Expiration Period */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Expiration Period
              </label>
              <select
                value={filters.expiration}
                onChange={(e) => onFiltersChange({ ...filters, expiration: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Periods</option>
                <option value="expired">Expired</option>
                <option value="expiring_soon">Expiring Soon (≤30 days)</option>
                <option value="expiring_later">Expiring Later (31-90 days)</option>
                <option value="valid">Valid (>90 days)</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Hide Quick Filters Toggle */}
      <div className="px-4 pb-2">
        <button
          onClick={() => setShowQuickFilters(!showQuickFilters)}
          className="text-xs text-gray-500 hover:text-gray-700"
        >
          {showQuickFilters ? 'Hide quick filters' : 'Show quick filters'}
        </button>
      </div>

      <style jsx>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default MobileSearchFilter;